import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchVoiceCompliance } from '../../../../store/trustHubSlice';
import CnamCard from '../components/voice/CnamCard';
import StirShakenCard from '../components/voice/StirShakenCard';
import VoiceIntegrityCard from '../components/voice/VoiceIntegrityCard';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import './VoiceComplianceTab.css';

const VoiceComplianceTab = ({ user }) => {
  const dispatch = useDispatch();
  const { voiceCompliance, voiceLoading, voiceError, dashboardData } = useSelector(
    (state) => state.trustHub
  );

  const [showInfoModal, setShowInfoModal] = useState(false);
  const userId = user?.user_id || user?.id;

  useEffect(() => {
    if (userId) {
      dispatch(fetchVoiceCompliance(userId));
    }
  }, [dispatch, userId]);

  // Get business profile status from Redux
  const profile = dashboardData?.business_profile || dashboardData?.profile;
  const profileStatus = profile?.status;
  const isProfileApproved = profileStatus &&
    ['approved', 'twilio-approved', 'verified'].includes(profileStatus.toLowerCase());

  // Re-fetch when business profile transitions to approved so STIR/SHAKEN auto-trigger is reflected
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (userId && isProfileApproved) {
      dispatch(fetchVoiceCompliance(userId));
    }
  }, [dispatch, userId, profileStatus]);

  // Check if STIR/SHAKEN is approved (normalize to lowercase to handle API casing variants)
  const isStirShakenApproved = voiceCompliance?.shakenStir?.status &&
    ['approved', 'twilio-approved', 'verified'].includes(voiceCompliance.shakenStir.status.toLowerCase());

  // CNAM and STIR/SHAKEN require approved business profile
  const isCnamEnabled = isProfileApproved && isStirShakenApproved;
  const isStirShakenEnabled = isProfileApproved;

  // Voice Integrity requires both approved profile and approved STIR/SHAKEN
  const isVoiceIntegrityEnabled = isProfileApproved && isStirShakenApproved;

  // Refresh data after updates
  const handleRefresh = () => {
    if (userId) {
      dispatch(fetchVoiceCompliance(userId));
    }
  };

  // Handle loading state
  if (voiceLoading && !voiceCompliance.cnam && !voiceCompliance.shakenStir && !voiceCompliance.voiceIntegrity) {
    return (
      <div className="voice-compliance-tab">
        <LoadingSkeleton variant="dashboard" />
      </div>
    );
  }

  // Handle error state
  if (voiceError) {
    return (
      <div className="voice-compliance-tab">
        <div className="alert alert-danger">
          <h4>Error Loading Voice Compliance</h4>
          <p>{voiceError}</p>
          <button
            className="btn linear-gradient zy-save-btn ml-2 mt-3"
            onClick={() => userId && dispatch(fetchVoiceCompliance(userId))}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="voice-compliance-tab">
      {/* Header */}
      <div className="voice-compliance-header">
        <div>
          <h2>
            Voice Compliance Management
            
          </h2>
          <p className="subtitle">STIR/SHAKEN registration must be completed before creating CNAM or Voice Integrity.</p>
        </div>
      </div>

      {/* Compliance Cards Grid */}
      <div className="voice-compliance-grid">
        {/* STIR/SHAKEN Card - Requires approved business profile */}
        <StirShakenCard
          userId={userId}
          stirShakenData={voiceCompliance.shakenStir}
          onUpdate={handleRefresh}
          disabled={!isStirShakenEnabled}
        />

        {/* CNAM Card - Requires approved profile and STIR/SHAKEN */}
        <CnamCard
          userId={userId}
          cnamData={voiceCompliance.cnam}
          onUpdate={handleRefresh}
          disabled={!isCnamEnabled}
        />

        {/* Voice Integrity Card - Requires approved profile and STIR/SHAKEN */}
        <VoiceIntegrityCard
          userId={userId}
          voiceIntegrityData={voiceCompliance.voiceIntegrity}
          onUpdate={handleRefresh}
          disabled={!isVoiceIntegrityEnabled}
          isProfileApproved={isProfileApproved}
        />
      </div>

      {/* Info Modal */}
      {showInfoModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={() => setShowInfoModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '10px',
              padding: '30px',
              maxWidth: '800px',
              maxHeight: '80vh',
              overflowY: 'auto',
              position: 'relative',
              border: '1px solid #dcdcdc'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#495057', fontSize: '20px', fontWeight: 600 }}>About Voice Compliance</h3>
              <i
                className="la la-times"
                style={{
                  fontSize: '18px',
                  cursor: 'pointer',
                  color: '#6c757d',
                   fontFamily:'LineAwesome'
                }}
                onClick={() => setShowInfoModal(false)}
              ></i>
            </div>
            <div className="info-grid" style={{ display: 'grid', gap: '20px' }}>
              <div className="info-card" style={{ padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                <h4 style={{ marginTop: 0, color: '#087ab6', fontSize: '16px', fontWeight: 600 }}>1. STIR/SHAKEN (Authentication)</h4>
                <p style={{ margin: '10px 0', color: '#495057', fontSize: '14px', lineHeight: '1.6' }}>
                  <strong>Caller ID authentication framework</strong> that prevents spoofing by adding a digital signature to calls.
                  Provides attestation levels (A, B, or C) to verify call origin. FCC-mandated for carriers.
                </p>
                <p style={{ margin: '10px 0 0', color: '#6c757d', fontSize: '13px', fontStyle: 'italic' }}>
                  Requires: Approved Business Profile
                </p>
              </div>
              <div className="info-card" style={{ padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                <h4 style={{ marginTop: 0, color: '#087ab6', fontSize: '16px', fontWeight: 600 }}>2. CNAM (Caller ID Name)</h4>
                <p style={{ margin: '10px 0', color: '#495057', fontSize: '14px', lineHeight: '1.6' }}>
                  Register your business name to appear on recipient's caller ID displays.
                  This helps increase answer rates and builds trust with your customers.
                </p>
                <p style={{ margin: '10px 0 0', color: '#6c757d', fontSize: '13px', fontStyle: 'italic' }}>
                  Requires: Approved Business Profile & Approved STIR/SHAKEN
                </p>
              </div>
              <div className="info-card" style={{ padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                <h4 style={{ marginTop: 0, color: '#087ab6', fontSize: '16px', fontWeight: 600 }}>3. Voice Integrity (Reputation)</h4>
                <p style={{ margin: '10px 0', color: '#495057', fontSize: '14px', lineHeight: '1.6' }}>
                  <strong>Spam prevention service</strong> that registers your verified business with carrier analytics engines.
                  Uses your STIR/SHAKEN registration to ensure legitimate calls aren't flagged as spam, improving answer rates.
                </p>
                <p style={{ margin: '10px 0 0', color: '#6c757d', fontSize: '13px', fontStyle: 'italic' }}>
                  Requires: Approved Business Profile & Approved STIR/SHAKEN
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceComplianceTab;
